import { c, msgid, ngettext } from 'ttag';

import { formatList } from '@freelancelabs/shared';

import { ContactOnboardingStatus, DataScope } from '../../../graphql/__generated__/schema';
import { EMPTY } from '../display';
import { contactStatusReading } from './contactLookup';
import { OnboardingRole, findRole, isRefused } from './grantableRoles';

/**
 * What `accountActivities` pre-ticked on one account. Carries the address it was computed for, so
 * the pre-ticking is an initialisation once per (account × address): unticking sticks.
 */
export interface ActivitySuggestion {
    /** The address it was computed for. A different one means it must be redone. */
    email: string;
    // Exactly what the pre-ticking ADDED, by type, never what the source designates: applyPatch
    // drops these and keeps hand-ticked ones. No counter: the reason lives on the row (the badge).
    needIds: string[];
    serviceIds: string[];
    /** How many ingested contacts the address matched. Above one is a shared mailbox mixing two people's business. */
    matchedContactCount: number;
}

/** What one account will carry: a role and a perimeter. A list, since these belong to (person × account). */
export interface DraftAttachment {
    organizationId: string;
    /** Null until picked: a role posted by default would be a role nobody chose. */
    roleId: string | null;
    // Still ALL or RESTRICTED, but a restriction is now on activities, not markets; the rename is a lot of its own.
    dataScope: DataScope;
    // The ticked activities, empty when ALL. Either list suffices when RESTRICTED: a perimeter may hold only needs.
    needIds: string[];
    serviceIds: string[];
    /** What was proposed, and for whom. Null until applied, and again when the perimeter leaves RESTRICTED. */
    suggestion: ActivitySuggestion | null;
}

// Start from the current selection, drop the previous suggestion (stale after a corrected address),
// add the new one. Everything ticked by hand survives.
const unionWithoutStale = (current: string[], previouslySuggested: string[], suggested: string[]): string[] => {
    const stale = new Set(previouslySuggested);
    const decidedByHand = current.filter((id) => !stale.has(id));
    return [...new Set([...decidedByHand, ...suggested])];
};

export const initialActivityIds = (
    attachment: DraftAttachment,
    suggestedNeeds: string[],
    suggestedServices: string[]
): { needIds: string[]; serviceIds: string[] } => ({
    needIds: unionWithoutStale(attachment.needIds, attachment.suggestion?.needIds ?? [], suggestedNeeds),
    serviceIds: unionWithoutStale(attachment.serviceIds, attachment.suggestion?.serviceIds ?? [], suggestedServices),
});

/**
 * What the contact's own accounts pre-ticked at step 2, and for which address. Lives on the draft
 * because an unticked suggestion has no attachment to hang from. Its list is only what it added.
 */
export interface AccountSuggestion {
    /** The address it was resolved for. A different one means it must be redone. */
    email: string;
    /** Exactly the accounts the pre-ticking added, in the order it added them. */
    accountIds: string[];
}

/**
 * One dashboard to grant. The account travels with the id so step 2 can drop dashboards whose
 * account has left the selection (refused out of scope at the send).
 */
export interface DraftDashboard {
    id: string;
    organizationId: string;
}

/**
 * The contact picked at step 1: the identity as the source writes it. Carries the address it was
 * resolved for, so applyPatch nulls a stale one and downstream reads it as current or null.
 */
export interface DraftContact {
    // Our contact uuid, sent with the invitation (contactId ID!). The API writes contact_onboarding from it.
    id: string;
    /** The address it was resolved for. A different one means it must be picked again. */
    email: string;
    firstName: string | null;
    lastName: string | null;
    /** Verbatim from the source: real job titles, tidying them would invent others. */
    jobFunction: string | null;
    /** The business reference the source carries. Shown, never sent. */
    businessReference: string | null;
    // The scale the API arbitrates, kept so the recap can re-read it. Never re-derived.
    onboardingStatus: ContactOnboardingStatus;
}

export interface Draft {
    email: string;
    /** Null until a contact is picked. Step 1 blocks until it is not. */
    contact: DraftContact | null;
    attachments: DraftAttachment[];
    /** What the contact's accounts pre-ticked, and for which address. Null until applied. */
    accountSuggestion: AccountSuggestion | null;
    /** Optional step: an empty list is « rien attribué », not « pas encore décidé ». */
    dashboards: DraftDashboard[];
    /** Optional step. One list at most: `InviteUserInput` carries a single id. */
    partnerListId: string | null;
}

// An empty draft, or seeded with an address when arriving from "Add an account". Only the address
// is seeded; the contact is still chosen through step 1's search.
export const emptyDraft = (email = ''): Draft => ({
    email,
    contact: null,
    attachments: [],
    accountSuggestion: null,
    dashboards: [],
    partnerListId: null,
});

export const STEP_COUNT = 5;

// After a changed address: drop whatever the previous suggestion ticked, keep everything ticked by hand.
const withoutStaleSuggestion = (attachment: DraftAttachment): DraftAttachment => {
    if (!attachment.suggestion) {
        return attachment;
    }
    const staleNeeds = new Set(attachment.suggestion.needIds);
    const staleServices = new Set(attachment.suggestion.serviceIds);
    return {
        ...attachment,
        needIds: attachment.needIds.filter((id) => !staleNeeds.has(id)),
        serviceIds: attachment.serviceIds.filter((id) => !staleServices.has(id)),
        suggestion: null,
    };
};

/**
 * The single place the draft changes: nothing resolved from one address may survive into another.
 * Three things are resolved from the address (pre-ticked markets, identity, pre-ticked accounts) and
 * all go stale together. It lives on the patch, not the row, because "Modifier" can correct the
 * address without step 2 ever rendering again; the identity is worse, sent verbatim to inviteUser.
 */
export const applyPatch = (draft: Draft, patch: Partial<Draft>): Draft => {
    const next = { ...draft, ...patch };
    if (next.email.trim() === draft.email.trim()) {
        return next;
    }
    // Compared against the new address: a suggestion carrying it is being resolved, not a leftover.
    const staleAccounts =
        next.accountSuggestion && next.accountSuggestion.email.trim() !== next.email.trim()
            ? new Set(next.accountSuggestion.accountIds)
            : null;
    return {
        ...next,
        // Compared against the new address: picking a contact and setting the address in one patch
        // is how step 1 records its choice.
        contact: next.contact && next.contact.email.trim() === next.email.trim() ? next.contact : null,
        accountSuggestion: staleAccounts ? null : next.accountSuggestion,
        attachments: next.attachments
            .filter((attachment) => !staleAccounts?.has(attachment.organizationId))
            .map(withoutStaleSuggestion),
        // Same clean-up as removeAttachment: a dashboard whose account left is refused out of scope at the send.
        dashboards: next.dashboards.filter((dashboard) => !staleAccounts?.has(dashboard.organizationId)),
    };
};

// Deliberately loose: the invitation arriving is the real check, so only refuse what plainly cannot be an address.
const looksLikeEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

// Roles that stop being grantable AFTER the pick (the actor's membership can change mid-journey).
// Silent while roles is loading or failed: isRefused only refuses a role it holds.
const notGrantableBlocker = (draft: Draft, roles: OnboardingRole[]): string | null => {
    const names = [
        ...new Set(
            draft.attachments
                .filter((attachment) => isRefused(roles, attachment.roleId))
                .map((attachment) => findRole(roles, attachment.roleId)?.name ?? EMPTY)
        ),
    ];
    if (names.length === 0) {
        return null;
    }
    const list = formatList(names);
    return ngettext(
        msgid`The role ${list} is no longer one you can grant. Pick another one: the invitation would be refused when sent.`,
        `The roles ${list} are no longer ones you can grant. Pick others: the invitation would be refused when sent.`,
        names.length
    );
};

// Why the operator cannot move on, or null. Printed next to the disabled footer button.
export const stepBlocker = (step: number, draft: Draft, roles: OnboardingRole[]): string | null => {
    if (step === 1) {
        if (!draft.email.trim()) {
            return c('Onboarding blocker').t`Enter the email address of the person to onboard.`;
        }
        if (!looksLikeEmail(draft.email)) {
            return c('Onboarding blocker').t`This email address is not valid.`;
        }
        // Only a known, picked contact lets the journey move on. applyPatch already nulled an
        // identity resolved for another address, so this also catches a corrected address.
        if (!draft.contact) {
            return c('Onboarding blocker')
                .t`Search the address and pick the contact to onboard: the identity comes from the source, not from this screen.`;
        }
        if (!contactStatusReading(draft.contact.onboardingStatus).selectable) {
            // Reachable when the contact's answer changed under the draft (a re-search or ingestion).
            return c('Onboarding blocker')
                .t`The contact picked can no longer be onboarded. Search the address again and read the reason given next to them.`;
        }
        return null;
    }

    if (step === 2) {
        if (draft.attachments.length === 0) {
            return c('Onboarding blocker').t`Add at least one account.`;
        }
        if (draft.attachments.some((attachment) => !attachment.roleId)) {
            return c('Onboarding blocker').t`Pick a role on every account.`;
        }
        // The API refuses it too; saying it here spares a round trip and names the account.
        if (
            draft.attachments.some(
                (attachment) =>
                    attachment.dataScope === DataScope.Restricted &&
                    attachment.needIds.length === 0 &&
                    attachment.serviceIds.length === 0
            )
        ) {
            return c('Onboarding blocker')
                .t`A restricted perimeter needs at least one activity, otherwise the person sees nothing.`;
        }
        return notGrantableBlocker(draft, roles);
    }

    if (step === STEP_COUNT) {
        // One block, re-running the earlier steps' checks rather than trusting they held when those
        // steps were left: "Revenir au récapitulatif" can reach here without step 1 or 2 rendering
        // again, so a stale identity, an empty restricted perimeter or a role that stopped being
        // grantable can arrive at the one screen that sends.
        return stepBlocker(1, draft, roles) ?? stepBlocker(2, draft, roles);
    }

    // Steps 3 and 4 never block: both attributions are optional.
    return null;
};
